
import React, { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Upload, FileText, Database, Trash2, Download, Search, Filter, BarChart3, MapPin, Droplets, Thermometer, Copy, Link} from 'lucide-react'
import { useMicrocuencasCRUD } from '../hooks/useMicrocuencasCRUD'
import { useJerarquizacionCRUD } from '../hooks/useJerarquizacionCRUD'
import { useMicrocuencasDetalleCRUD } from '../hooks/useMicrocuencasDetalleCRUD'
import { useMicrocuencasCompletoCRUD } from '../hooks/useMicrocuencasCompletoCRUD'
import { useMicrocuencasVariablesCRUD } from '../hooks/useMicrocuencasVariablesCRUD'

const DatabaseUpload = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterMunicipio, setFilterMunicipio] = useState('Todos')
  const [dragActive, setDragActive] = useState(false)
  const [autoImported, setAutoImported] = useState(false)
  
  const {
    microcuencas,
    loading,
    uploading,
    uploadCSVFile,
    uploadCSVFromURL,
    deleteMicrocuenca,
    clearAllData,
    removeDuplicates
  } = useMicrocuencasCRUD()

  const {
    clearAllData: clearJerarquizacion
  } = useJerarquizacionCRUD()

  const {
    microcuencasDetalle,
    uploading: uploadingDetalle,
    uploadCSVFromURL: uploadDetalleFromURL,
    clearAllData: clearDetalle
  } = useMicrocuencasDetalleCRUD()

  const {
    uploading: uploadingCompleto,
    uploadCSVFromURL: uploadCompletoFromURL,
    clearAllData: clearCompleto
  } = useMicrocuencasCompletoCRUD()

  const {
    uploading: uploadingVariables,
    uploadCSVFromURL: uploadVariablesFromURL,
    clearAllData: clearVariables
  } = useMicrocuencasVariablesCRUD()

  const handleImportFromURL = async () => {
    const csvUrl = 'https://static.lumi.new/material/5e/5e60b58d6fefb2fb2fa9324d72749116.csv'
    const txtUrl = 'https://static.lumi.new/material/b4/b492f69f5f21aede91746528f575d56b.txt'
    const completoUrl = 'https://file.lumi.new/p373487986422038528/SPF2011163801677901824.csv'
    // Datos actualizados 2024 con 79 microcuencas
    const variablesUrl = window.location.origin + '/datos_microcuencas_2024.txt'
    await Promise.all([
      uploadCSVFromURL(csvUrl),
      uploadDetalleFromURL(csvUrl),
      uploadCompletoFromURL(completoUrl),
      uploadVariablesFromURL(variablesUrl)
    ])
  }

  // Importar automáticamente si no hay datos
  useEffect(() => {
    if (!loading && !uploading && microcuencas.length === 0 && !autoImported) {
      setAutoImported(true)
      handleImportFromURL()
    }
  }, [loading, uploading, microcuencas.length, autoImported])

  const filteredData = microcuencas.filter(item => {
    const matchesSearch = 
      item.microcuenca.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.municipio.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.codigo.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesFilter = filterMunicipio === 'Todos' || item.municipio === filterMunicipio
    
    return matchesSearch && matchesFilter
  })

  const municipios = Array.from(new Set(microcuencas.map(m => m.municipio))).sort()

  const estadisticas = {
    totalRegistros: microcuencas.length,
    totalMunicipios: municipios.length,
    superficieTotal: microcuencas.reduce((sum, m) => sum + m.sup_micro, 0),
    precipitacionPromedio: microcuencas.length > 0 
      ? microcuencas.reduce((sum, m) => sum + m.precipitacion, 0) / microcuencas.length 
      : 0
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        await uploadCSVFile(file)
      } else {
        alert('Por favor selecciona un archivo CSV válido')
      }
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        await uploadCSVFile(file)
      } else {
        alert('Por favor selecciona un archivo CSV válido')
      }
      // Reset input
      e.target.value = ''
    }
  }

  const handleClearAll = async () => {
    if (confirm('¿Estás seguro de que quieres eliminar TODAS las bases de datos (Microcuencas, Microcuencas Detalle, Jerarquización, Microcuencas Completo y Microcuencas Variables)? Esta acción no se puede deshacer.')) {
      await Promise.all([
        clearAllData(),
        clearDetalle(),
        clearJerarquizacion(),
        clearCompleto(),
        clearVariables()
      ])
    }
  }

  const exportToCSV = () => {
    const headers = [
      'FID', 'Código', 'Municipio', 'Microcuenca', 'Superficie Agrícola',
      'Superficie Micro', 'Superficie Pasto', 'Superficie Forestal',
      'Densidad Población', 'Precipitación', 'Temperatura', 'Altitud', 'Pendiente'
    ]
    
    const csvContent = [
      headers.join(','),
      ...filteredData.map(item => [
        item.fid,
        item.codigo,
        `"${item.municipio}"`,
        `"${item.microcuenca}"`,
        item.superficie_agricola,
        item.sup_micro,
        item.superficie_pasto,
        item.superficie_forestal,
        item.densidad_poblacion,
        item.precipitacion,
        item.temperatura,
        item.altitud,
        item.pendiente
      ].join(','))
    ].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `microcuencas_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver al Inicio</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Database className="text-white w-4 h-4" />
                </div>
                <h1 className="text-xl font-bold text-gray-800">Base de Datos de Microcuencas</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={handleImportFromURL}
                disabled={uploading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Link className="w-4 h-4" />
                <span>Importar desde URL</span>
              </button>
              <button 
                onClick={exportToCSV}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Exportar</span>
              </button>
              {microcuencas.length > 0 && (
                <>
                  <button 
                    onClick={() => removeDuplicates()}
                    className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Quitar Duplicados</span>
                  </button>
                  <button 
                    onClick={handleClearAll}
                    className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Eliminar Todas las BD</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-6 py-8">
        {/* Estadísticas Generales */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Registros</p>
                <p className="text-3xl font-bold text-blue-600">{estadisticas.totalRegistros}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Municipios</p>
                <p className="text-3xl font-bold text-green-600">{estadisticas.totalMunicipios}</p>
              </div>
              <MapPin className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Superficie Total</p>
                <p className="text-3xl font-bold text-purple-600">{estadisticas.superficieTotal.toFixed(0)}</p>
                <p className="text-gray-500 text-xs">hectáreas</p>
              </div>
              <Database className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Precipitación Prom.</p>
                <p className="text-3xl font-bold text-orange-600">{estadisticas.precipitacionPromedio.toFixed(0)}</p>
                <p className="text-gray-500 text-xs">mm/año</p>
              </div>
              <Droplets className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </motion.div>

        {/* Zona de Carga de Archivos */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-8 mb-8"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">Cargar Archivo CSV</h2>
          
          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            
            <h3 className="text-xl font-semibold mb-2">
              {uploading ? 'Procesando archivo...' : 'Arrastra tu archivo CSV aquí'}
            </h3>
            
            <p className="text-gray-600 mb-4">
              O haz clic para seleccionar un archivo
            </p>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Cargando...' : 'Seleccionar Archivo'}
            </button>
            
            <div className="mt-4 text-sm text-gray-500">
              <p>Formato esperado: fid, CODIGO, MUNICIPIO, MICROCUENCA, Superficie Agricola, Sup_Micro_, Superficie Pasto, Superficie Forestal, Densidad de población, Precipitación, Temperatura, Altitud, Pendiente</p>
            </div>
          </div>
        </motion.div>

        {/* Controles de Filtrado */}
        {microcuencas.length > 0 && (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg p-6 mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Buscar microcuenca, municipio o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-80"
                  />
                </div>
                
                <select
                  value={filterMunicipio}
                  onChange={(e) => setFilterMunicipio(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Todos">Todos los municipios</option>
                  {municipios.map(municipio => (
                    <option key={municipio} value={municipio}>{municipio}</option>
                  ))}
                </select>
              </div>
              
              <div className="text-sm text-gray-600">
                Mostrando {filteredData.length} de {microcuencas.length} registros
              </div>
            </div>
          </motion.div>
        )}

        {/* Tabla de Datos */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : microcuencas.length === 0 ? (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white rounded-xl shadow-lg p-12 text-center"
          >
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No hay datos disponibles</h3>
            <p className="text-gray-600">Carga un archivo CSV para comenzar a visualizar los datos</p>
          </motion.div>
        ) : (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">FID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Municipio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Microcuenca</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sup. Agrícola</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sup. Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precipitación</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Temperatura</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.map((item) => (
                    <motion.tr 
                      key={item._id}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.fid}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.codigo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.municipio}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.microcuenca}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.superficie_agricola.toFixed(1)} ha
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.sup_micro.toFixed(1)} ha
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.precipitacion.toFixed(1)} mm
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.temperatura.toFixed(1)}°C
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => deleteMicrocuenca(item._id!)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Eliminar registro"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default DatabaseUpload
